import React from 'react';
import styled, { keyframes } from 'styled-components';
import { activityHeader, activityIconSlot, activityRow, shimmerText } from '../chat/activityShimmer';

export interface LoadingMessageProps {
  // When provided, displayed next to the spinner. Use for contextual
  // status that the tool indicators don't already cover — e.g. the
  // OCR/extraction phase before chat generation actually starts, where
  // no tool_action item is emitted.
  loadingMessage?: string;
}

function LoadingMessage({ loadingMessage }: LoadingMessageProps) {
  // Spinner-only by default. The ONE exception is the file-processing phase
  // (upload + text extraction), which runs before generation starts and has
  // no tool-action block of its own — surface that text so the user knows what
  // is taking time. Generation/reasoning/tool statuses stay spinner-only.
  const msg = loadingMessage || '';
  // Deep-research planning has no block of its own either (like file-processing),
  // so surface its text instead of a bare spinner.
  const showText = msg.startsWith('Extraindo') || msg.startsWith('Fazendo upload') || msg.startsWith('Criando plano');
  // Sem texto, um rótulo de largura zero mantém a MESMA altura de linha: a
  // troca "Extraindo texto…" → spinner puro não pode encolher a linha.
  return (
    <Row>
      <Header>
        <IconSlot><Ring /></IconSlot>
        {showText ? <Label>{msg}</Label> : <Label aria-hidden>{'\u200b'}</Label>}
      </Header>
    </Row>
  );
}

export default LoadingMessage;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Mesma caixa do ThinkingGroup/ExtractingFilesBlock (ver activityShimmer).
const Row = styled.div`
  ${activityRow}
`;

const Header = styled.div`
  ${activityHeader}
`;

const IconSlot = styled.span`
  ${activityIconSlot}
`;

const Ring = styled.div`
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => (theme.mode === 'dark'
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.12)')};
  border-top-color: ${({ theme }) => theme.text_secondary || theme.font_color};
  animation: ${spin} 0.75s linear infinite;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Label = styled.span`
  /* Always an in-progress state when text is shown — shimmer like the rest. */
  ${shimmerText}
`;
